import { useContext, useEffect } from "react";
import {
  createDigiConLink,
  createFlashUILink,
  createPaystationLink,
  createRaceLink,
  createSubsLink,
  createTaxLink,
} from "../helpers/linkUtils";
import {
  AccountingEventsGroup,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../types";
import { OrderIdContext, StageContext } from "../contexes/OrderIdContext";
import { useMarketplaceId } from "../contexes/MarketplaceIdContext";
import { useDeferralId } from "../contexes/DeferralIdContext";

interface UseExternalLinksProps {
  accountingEventsGroup?: AccountingEventsGroup;
  digitalBusinessDocument?: DigitalBusinessDocument;
  digitalCostDocument?: DigitalCostDocument;
  digitalPaymentDocument?: DigitalPaymentDocument;
}

export function useExternalLinks({
  accountingEventsGroup,
  digitalBusinessDocument,
  digitalCostDocument,
  digitalPaymentDocument,
}: UseExternalLinksProps) {
  const orderId = useContext(OrderIdContext);
  const stage = useContext(StageContext);
  const { setMarketplaceId } = useMarketplaceId();
  const { deferralIds } = useDeferralId();

  const getMarketplaceId = () => {
    if (accountingEventsGroup?.accountingEvents) {
      const firstEvent = Object.values(
        accountingEventsGroup.accountingEvents
      )[0];
      return firstEvent?.marketplaceId || "";
    }
    if (digitalBusinessDocument) {
      return digitalBusinessDocument.body.header.marketplaceId;
    }
    if (digitalCostDocument) {
      return digitalCostDocument.body.header.marketplaceId;
    }
    if (digitalPaymentDocument) {
      return digitalPaymentDocument.body.header.marketplaceId;
    }
    return "";
  };

  const getDigitalOrderId = () => {
    if (accountingEventsGroup?.accountingEvents) {
      const firstEvent = Object.values(
        accountingEventsGroup.accountingEvents
      )[0];
      return firstEvent?.orderId || "";
    }
    if (digitalBusinessDocument) {
      return digitalBusinessDocument.body.header.id;
    }
    if (digitalCostDocument) {
      return digitalCostDocument.body.header.id;
    }
    if (digitalPaymentDocument) {
      return digitalPaymentDocument.body.header.id;
    }
    return "";
  };

  const getPaymentContractRevisionId = () => {
    if (digitalBusinessDocument?.body?.body.payments) {
      return Object.keys(digitalBusinessDocument.body.body.payments)[0] || "";
    }
    if (accountingEventsGroup?.accountingEvents) {
      const idFromFinanceTag = Object.values(
        accountingEventsGroup.accountingEvents
      )
        .map((event) => event.financeTagAuthReferenceId)
        .find((id) => id);

      if (idFromFinanceTag) {
        return idFromFinanceTag.replace(/-\d+$/, "");
      }

      const contractId = Object.values(accountingEventsGroup.accountingEvents)
        .map((event) => {
          if (event.paymentContractRevisionIds) {
            return Object.values(event.paymentContractRevisionIds)[0];
          }
          return null;
        })
        .find((id) => id);

      return contractId ? contractId.replace(/_\d+$/, "") : "";
    }
    return "";
  };

  const getSubscriptionId = () => {
    if (accountingEventsGroup?.accountingEvents) {
      const firstEvent = Object.values(
        accountingEventsGroup.accountingEvents
      )[0];
      return firstEvent?.subscriptionId || "";
    }
    return "";
  };

  const getTaxTransactionId = () => {
    if (digitalBusinessDocument && digitalBusinessDocument.body) {
      const { products } = digitalBusinessDocument.body.body;
      const firstProduct = Object.values(products ?? {})[0];
      if (firstProduct) {
        const firstBillId = Object.keys(firstProduct.bills)[0];
        if (firstBillId) {
          return firstProduct.bills[firstBillId].taxId || "";
        }
      }
    }

    if (accountingEventsGroup?.accountingEvents) {
      const firstEvent = Object.values(
        accountingEventsGroup.accountingEvents
      )[0];
      return firstEvent?.taxTransactionId || "";
    }
    return "";
  };

  const marketplaceId = getMarketplaceId();
  const digitalOrderId = getDigitalOrderId();
  const paymentContractRevisionId = getPaymentContractRevisionId();
  const subscriptionId = getSubscriptionId();
  const taxId = getTaxTransactionId();

  const hasRevenueLink = !!deferralIds.revenueDeferralId;
  const hasRoyaltyLink =
    deferralIds.royaltyDeferralId &&
    deferralIds.royaltyDeferralId !== deferralIds.revenueDeferralId;
  const showBothRaceLinks = hasRevenueLink && hasRoyaltyLink;

  useEffect(() => {
    if (marketplaceId) {
      setMarketplaceId(marketplaceId);
    }
  }, [marketplaceId, setMarketplaceId]);

  const getLinks = () => {
    const links = [
      {
        key: "flash-ui",
        href: createFlashUILink(marketplaceId, orderId, stage),
        testId: "FlashLink",
        text: "Flash UI",
      },
    ];

    if (hasRevenueLink) {
      links.push({
        key: "race-revenue",
        href: createRaceLink(
          marketplaceId,
          deferralIds.revenueDeferralId,
          stage
        ),
        testId: "RaceRevenueLink",
        text: showBothRaceLinks ? "Race Revenue" : "Race",
      });
    }

    if (hasRoyaltyLink) {
      links.push({
        key: "race-royalty",
        href: createRaceLink(
          marketplaceId,
          deferralIds.royaltyDeferralId,
          stage
        ),
        testId: "RaceRoyaltyLink",
        text: "Race Royalty",
      });
    }

    if (digitalOrderId && !digitalPaymentDocument) {
      links.push({
        key: "digicon",
        href: createDigiConLink(marketplaceId, digitalOrderId),
        testId: "DigiconLink",
        text: "DigiCon",
      });
    }

    if (paymentContractRevisionId && marketplaceId) {
      links.push({
        key: "paystation",
        href: createPaystationLink(marketplaceId, paymentContractRevisionId),
        testId: "PaystationLink",
        text: "PayStation",
      });
    }

    const subsLink = createSubsLink(stage, marketplaceId, subscriptionId);
    if (subsLink) {
      links.push({
        key: "subscriptions",
        href: subsLink,
        testId: "SubscriptionsLink",
        text: "Subs",
      });
    }

    if (taxId) {
      links.push({
        key: "tax",
        href: createTaxLink(stage, marketplaceId, taxId),
        testId: "TaxLink",
        text: "Tax",
      });
    }

    return links;
  };

  return {
    links: getLinks(),
  };
}
